import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import { HelpCircle } from "lucide-react";

import AppTooltip from "../AppTooltip";
import { i18n } from "../../translate/i18n";

/**
 * Tooltip de ajuda padrão do sistema.
 *
 * Uso:
 *   <HelpTooltipIcon helpKey="settings.timezone.helps.companyTimezone" />
 *
 * Lê do i18n um objeto:
 *   { description: string, steps?: string[] }
 *
 * Também aceita `description` e `steps` diretos pra casos sem chave de tradução.
 *
 *   <HelpTooltipIcon description="..." steps={["a", "b"]} />
 *
 * Visualmente é um HelpCircle (lucide) translúcido que vira primary no hover,
 * abrindo um AppTooltip que herda o estilo do tema global.
 */
const useStyles = makeStyles((theme) => {
  const isDark = theme.mode === "dark" || theme.palette?.type === "dark";
  return {
    icon: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      verticalAlign: "middle",
      color: isDark ? "#8696a0" : "#667781",
      cursor: "help",
      opacity: 0.6,
      transition: "opacity 0.15s ease, color 0.15s ease",
      "&:hover": {
        opacity: 1,
        color: theme.palette.primary.main,
      },
    },
    title: {
      fontWeight: 600,
      marginBottom: 6,
      fontSize: 12,
    },
    steps: {
      margin: 0,
      paddingLeft: 16,
      "& li": { marginBottom: 3 },
      "& li:last-child": { marginBottom: 0 },
    },
  };
});

const buildContent = (classes, description, steps) => {
  const safeSteps = Array.isArray(steps) ? steps : [];
  if (!description && safeSteps.length === 0) return null;
  return (
    <div>
      {description && <div className={classes.title}>{description}</div>}
      {safeSteps.length > 0 && (
        <ol className={classes.steps}>
          {safeSteps.map((s, i) => (
            <li key={i}>{s}</li>
          ))}
        </ol>
      )}
    </div>
  );
};

const HelpTooltipIcon = ({
  helpKey,
  description: descProp,
  steps: stepsProp,
  size = 14,
  placement = "top",
}) => {
  const classes = useStyles();

  let description = descProp;
  let steps = stepsProp;

  if (helpKey && (!description || !steps)) {
    const data = i18n.t(helpKey, { returnObjects: true, defaultValue: null });
    if (data && typeof data === "object") {
      description = description ?? data.description;
      steps = steps ?? data.steps;
    }
  }

  const content = buildContent(classes, description, steps);
  if (!content) return null;

  return (
    <AppTooltip
      placement={placement}
      title={content}
      enterTouchDelay={0}
      leaveTouchDelay={6000}
    >
      <span className={classes.icon}>
        <HelpCircle size={size} strokeWidth={2} />
      </span>
    </AppTooltip>
  );
};

export default HelpTooltipIcon;
